/**
 * graphene_tool_utils.ts
 * Utility to deactivate graphene multicut / merge tools in the viewer.
 * Shared between SplitMergeOverlay.vue and main.ts (Escape handler).
 */

/**
 * Detect which graphene tool is currently active.
 * Returns 'multicut' | 'merge' | null.
 */
export function activeGrapheneTool(): 'multicut' | 'merge' | null {
  if (document.querySelector('.graphene-multicut')) return 'multicut';
  if (document.querySelector('.graphene-merge-segments')) return 'merge';
  return null;
}

/**
 * Deactivate the active graphene multicut or merge tool.
 *
 * Strategies in priority order:
 * 1. Deactivate via the viewer's tool binder / layer tool API
 * 2. Re-dispatch the activation keybinding (toggle off)
 * 3. Click NG's native close button in the tool side panel
 * 4. Dispatch Escape to the NG viewer display element
 * 5. Hide the selected layer panel as last resort
 */
export function exitGrapheneTool() {
  const viewer = (window as any)['viewer'];
  if (!viewer) return;

  // ── Strategy 1: Deactivate via viewer layer tool API ────────────────
  // Try multiple paths since NG's internal structure varies.
  try {
    // Path A: selectedLayer → managed layer → user layer → tool
    const ml = viewer.selectedLayer?.layer;
    const ul = ml?.layer;
    if (ul?.tool?.value != null) {
      console.info('[graphene_tool_utils] Clearing tool via selectedLayer tool.value');
      ul.tool.value = undefined;
      return;
    }
    // Path B: toolBinder on the user layer
    if (ul?.toolBinder?.deactivate) {
      console.info('[graphene_tool_utils] Deactivating via toolBinder.deactivate()');
      ul.toolBinder.deactivate();
      return;
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Strategy 1a (selectedLayer) failed:', e);
  }

  // Walk ALL managed layers in case selectedLayer isn't the right one
  try {
    const managedLayers = viewer.layerManager?.managedLayers ?? [];
    for (const ml of managedLayers) {
      const ul = ml.layer;
      if (!ul) continue;
      if (ul.tool?.value != null) {
        console.info('[graphene_tool_utils] Clearing tool on layer:', ml.name);
        ul.tool.value = undefined;
        return;
      }
      if (ul.toolBinder?.deactivate) {
        console.info('[graphene_tool_utils] toolBinder.deactivate() on layer:', ml.name);
        ul.toolBinder.deactivate();
        return;
      }
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Strategy 1b (iterate layers) failed:', e);
  }

  // ── Strategy 2: (REMOVED — dispatching toggle key C/M is racy) ──────
  // The custom keybindings always call viewer.activateTool() (never toggle),
  // so dispatching C/M after NG's Escape handler already deactivated the tool
  // would REACTIVATE it. Skip straight to Strategy 3.

  // If NG already handled the deactivation (tool DOM gone), we're done
  if (!activeGrapheneTool()) {
    console.info('[graphene_tool_utils] Tool already deactivated (by NG), nothing to do');
    return;
  }

  // ── Strategy 3: Click NG's native close/back button ─────────────────
  try {
    const toolEl = document.querySelector('.graphene-multicut')
                || document.querySelector('.graphene-merge-segments');
    if (toolEl) {
      const panel = toolEl.closest('.neuroglancer-layer-side-panel-info-tab')
                 || toolEl.closest('.neuroglancer-layer-side-panel')
                 || toolEl.closest('.neuroglancer-side-panel');
      if (panel) {
        // Try various close/back button selectors
        const closeBtn = panel.querySelector('[title*="close"]')
                      || panel.querySelector('[title*="Close"]')
                      || panel.querySelector('[title*="back"]')
                      || panel.querySelector('[title*="Back"]')
                      || panel.querySelector('button.neuroglancer-icon')
                      || panel.querySelector('.neuroglancer-icon');
        if (closeBtn) {
          console.info('[graphene_tool_utils] Clicking close button in panel');
          (closeBtn as HTMLElement).click();
          return;
        }
      }
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Strategy 3 (click button) failed:', e);
  }

  // ── Strategy 4: Dispatch Escape to NG's display element ─────────────
  try {
    const target = viewer.display?.element
      || document.getElementById('neuroglancer-container')
      || document.querySelector('.neuroglancer-viewer');
    if (target) {
      console.info('[graphene_tool_utils] Dispatching Escape to NG display');
      target.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Escape', code: 'Escape', bubbles: true, cancelable: true,
      }));
      return;
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Strategy 4 (dispatch Escape) failed:', e);
  }

  // ── Strategy 5: Hide the layer panel as absolute last resort ────────
  try {
    console.info('[graphene_tool_utils] Fallback — hiding selectedLayer panel');
    viewer.selectedLayer.visible = false;
  } catch {}
}
